import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupSubmenuToggle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Reset submenu container when closing main drawer
    if (details === this.refs.details) {
      const submenuContainer = this.querySelector('#menu-drawer-submenu-container');
      if (submenuContainer) {
        submenuContainer.innerHTML = '';
      }
      const submenuButtons = this.querySelectorAll('.menu-drawer__menu-item--with-submenu');
      submenuButtons.forEach((btn) => btn.classList.remove('menu-drawer__menu-item--active-submenu'));
      const rightNavigation = this.querySelector('.menu-drawer__navigation--right');
      if (rightNavigation instanceof HTMLElement) {
        rightNavigation.classList.remove('menu-drawer__navigation--right--visible');
      }
    }

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Setup submenu toggle functionality for two-column layout
   */
  #setupSubmenuToggle() {
    const submenuButtons = this.querySelectorAll('.menu-drawer__menu-item--with-submenu');
    const submenuContainer = this.querySelector('#menu-drawer-submenu-container');
    const allSubmenus = this.querySelectorAll('.menu-drawer__submenu-right');

    submenuButtons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        const handle = button.dataset.submenuHandle;
        const targetSubmenu = this.querySelector(`.menu-drawer__submenu-right[data-submenu-handle="${handle}"]`);

        if (!targetSubmenu || !submenuContainer || !(targetSubmenu instanceof HTMLElement)) return;

        // Get the right column navigation
        const rightNavigation = submenuContainer.closest('.menu-drawer__navigation--right');
        
        // Check if this button is already active (toggle behavior)
        const isActive = button.classList.contains('menu-drawer__menu-item--active-submenu');
        const currentSubmenu = submenuContainer.querySelector('.menu-drawer__submenu-right');
        const isCurrentSubmenu = currentSubmenu instanceof HTMLElement && currentSubmenu.dataset.submenuHandle === handle;

        // Use requestAnimationFrame to ensure state is set after any other handlers
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
          // If clicking the same active button, hide submenu and right column (toggle off)
          if (isActive && isCurrentSubmenu) {
            submenuContainer.innerHTML = '';
            button.classList.remove('menu-drawer__menu-item--active-submenu');
            if (rightNavigation instanceof HTMLElement) {
              rightNavigation.classList.remove('menu-drawer__navigation--right--visible');
            }
            return;
          }

          // Remove active state from all buttons
          submenuButtons.forEach((btn) => {
            if (btn instanceof HTMLElement) {
              btn.classList.remove('menu-drawer__menu-item--active-submenu');
            }
          });

          // Hide all submenus
          allSubmenus.forEach((submenu) => {
            if (submenu instanceof HTMLElement) {
              submenu.hidden = true;
            }
          });

          // Show right column
          if (rightNavigation instanceof HTMLElement) {
            rightNavigation.classList.add('menu-drawer__navigation--right--visible');
          }

          // Show selected submenu in right column
          submenuContainer.innerHTML = '';
          const clonedSubmenu = targetSubmenu.cloneNode(true);
          if (clonedSubmenu instanceof HTMLElement && handle) {
            clonedSubmenu.hidden = false;
            clonedSubmenu.classList.add('menu-drawer__submenu-right--visible');
            clonedSubmenu.setAttribute('data-submenu-handle', handle);
          }
          submenuContainer.appendChild(clonedSubmenu);

          // Add active state to button
          button.classList.add('menu-drawer__menu-item--active-submenu');
          });
        });
      }, { capture: true });
    });

  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
